// src/services/actionPlanService.js

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8000';

export const actionPlanService = {
  
  async getActionPlan(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/action-plan/${userId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 404) {
        console.log('No action plan found - needs initialization');
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch action plan: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Action plan loaded:', data);
      return data;
    } catch (error) {
      console.error('Error fetching action plan:', error);
      throw error;
    }
  },

  async initActionPlan(userId) {
    try {
      const response = await fetch(`${API_BASE_URL}/action-plan/${userId}/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to initialize action plan: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Action plan initialized:', result);
      return result;
    } catch (error) {
      console.error('Error initializing action plan:', error);
      throw error;
    }
  },

  async submitCheckIn(userId, potId, items) {
    try {
      const response = await fetch(`${API_BASE_URL}/action-plan/${userId}/checkin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pot_id: potId,
          items: items,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to submit check-in: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Check-in submitted:', result);
      return result;
    } catch (error) {
      console.error('Error submitting check-in:', error);
      throw error;
    }
  },

  async getTimeline(userId, limit = 20) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/action-plan/${userId}/timeline?limit=${limit}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch timeline: ${response.status}`);
      }

      const data = await response.json();
      console.log('✅ Timeline loaded:', data);
      return data;
    } catch (error) {
      console.error('Error fetching timeline:', error);
      throw error;
    }
  },

  async createObjective(userId, potId, title, initiatives) {
    try {
      const response = await fetch(`${API_BASE_URL}/action-plan/${userId}/objectives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pot_id: potId,
          title: title,
          initiatives: initiatives,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create objective: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Objective created:', result);
      return result;
    } catch (error) {
      console.error('Error creating objective:', error);
      throw error;
    }
  },

  async updateObjective(userId, objectiveId, title) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/action-plan/${userId}/objectives/${objectiveId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ title }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update objective: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Objective updated:', result);
      return result;
    } catch (error) {
      console.error('Error updating objective:', error);
      throw error;
    }
  },

  async completeObjective(userId, objectiveId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/action-plan/${userId}/objectives/${objectiveId}/complete`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to complete objective: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Objective completed:', result);
      return result;
    } catch (error) {
      console.error('Error completing objective:', error);
      throw error;
    }
  },

  // ✅ NEW: Undo objective completion
  async undoCompleteObjective(userId, objectiveId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/action-plan/${userId}/objectives/${objectiveId}/undo-complete`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to undo objective completion: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Objective completion undone:', result);
      return result;
    } catch (error) {
      console.error('Error undoing objective completion:', error);
      throw error;
    }
  },

  async createInitiative(userId, objectiveId, title, horizon, xpValue) {
    try {
      const response = await fetch(`${API_BASE_URL}/action-plan/${userId}/initiatives`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          objective_id: objectiveId,
          title: title,
          horizon: horizon,
          xp_value: xpValue,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create initiative: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Initiative created:', result);
      return result;
    } catch (error) {
      console.error('Error creating initiative:', error);
      throw error;
    }
  },

  async updateInitiative(userId, initiativeId, title) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/action-plan/${userId}/initiatives/${initiativeId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ title }),
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to update initiative: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Initiative updated:', result);
      return result;
    } catch (error) {
      console.error('Error updating initiative:', error);
      throw error;
    }
  },

  async deleteInitiative(userId, initiativeId) {
    try {
      const response = await fetch(
        `${API_BASE_URL}/action-plan/${userId}/initiatives/${initiativeId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to delete initiative: ${response.status}`);
      }

      const result = await response.json();
      console.log('✅ Initiative deleted:', result);
      return result;
    } catch (error) {
      console.error('Error deleting initiative:', error);
      throw error;
    }
  },
};

export default actionPlanService;
